import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Eye, EyeOff, Check, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { COUNTRY_LIST, type CountryOption } from '../data/countries';

const FLAG_CDN = 'https://flagcdn.com';
function FlagImg({ iso2, className }: { iso2: string; className?: string }) {
  return (
    <img
      src={`${FLAG_CDN}/w40/${iso2.toLowerCase()}.png`}
      alt=""
      className={className}
      width={24}
      height={18}
      loading="lazy"
    />
  );
}

// Industry-standard password rules (NIST / OWASP style)
const MIN_PASSWORD_LENGTH = 8;
const PASSWORD_RULES = {
  length: (p: string) => p.length >= MIN_PASSWORD_LENGTH,
  uppercase: (p: string) => /[A-Z]/.test(p),
  lowercase: (p: string) => /[a-z]/.test(p),
  number: (p: string) => /[0-9]/.test(p),
  special: (p: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p),
};

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [email, setEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(COUNTRY_LIST[0]);
  const [phone, setPhone] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ruleChecks = {
    length: PASSWORD_RULES.length(password),
    uppercase: PASSWORD_RULES.uppercase(password),
    lowercase: PASSWORD_RULES.lowercase(password),
    number: PASSWORD_RULES.number(password),
    special: PASSWORD_RULES.special(password),
  };
  const passwordValid = Object.values(ruleChecks).every(Boolean);
  const confirmMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fullPhone = `${selectedCountry.dialCode} ${phone.trim()}`.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!passwordValid) {
      setError('Password must meet all rules (8+ characters, upper, lower, number, special character).');
      return;
    }

    setLoading(true);

    try {
      const result = await signup(email, fullPhone, password, confirmPassword);
      if (result.success) {
        navigate('/login', { replace: true });
      } else {
        setError(result.error || 'Signup failed');
        setLoading(false);
      }
    } catch {
      setError('Signup failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Branding Panel */}
      <div className="hidden lg:flex w-1/2 bg-gray-50 items-center justify-center px-12">
        <div className="max-w-md">
        <img src="/logo_liaison.svg" alt="Liaison" className="h-5 w-50 mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Inventory Reconciliation Tool</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Get started with accurate inventory tracking. Our tool helps you identify
            discrepancies between your systems and maintain reliable stock data.
          </p>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-gray-900 flex-shrink-0" />
              <span className="text-gray-600">Weekly variance breakdown and analysis</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-gray-900 flex-shrink-0" />
              <span className="text-gray-600">Export reports in CSV format</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-gray-900 flex-shrink-0" />
              <span className="text-gray-600">Chat interface for data queries</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Right: Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create an account</h1>
          <p className="text-gray-500 mb-8">Fill in your details to get started</p>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition"
                placeholder="name@company.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-1.5">
                Phone number
              </label>
              <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-slate-400 focus-within:border-transparent transition" ref={countryDropdownRef}>
                <div className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setCountryDropdownOpen((o) => !o)}
                    className="flex items-center gap-1.5 px-3 h-full min-h-[48px] bg-gray-50 border-r border-gray-300 rounded-l-lg hover:bg-gray-100 text-gray-700 text-sm font-medium"
                    aria-label="Select country"
                    aria-expanded={countryDropdownOpen}
                    aria-haspopup="listbox"
                  >
                    <FlagImg iso2={selectedCountry.iso2} className="w-6 h-4 object-cover rounded-sm shrink-0" />
                    <span>{selectedCountry.dialCode}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {countryDropdownOpen && (
                    <ul
                      role="listbox"
                      className="absolute left-0 top-full z-10 mt-1 max-h-56 w-28 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                    >
                      {COUNTRY_LIST.map((c) => (
                        <li
                          key={c.iso2}
                          role="option"
                          aria-selected={c.iso2 === selectedCountry.iso2}
                          onClick={() => {
                            setSelectedCountry(c);
                            setCountryDropdownOpen(false);
                          }}
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 text-gray-900 text-sm"
                        >
                          <FlagImg iso2={c.iso2} className="w-6 h-4 object-cover rounded-sm shrink-0" />
                          <span className="font-medium">{c.dialCode}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex-1 min-w-0 px-4 py-3 rounded-r-lg text-gray-900 placeholder-gray-400 focus:outline-none border-0"
                  placeholder="555 000 0000"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-11 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition"
                  placeholder="Min. 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-gray-500 mb-1.5"></p>
              <ul className="space-y-1 text-xs">
                <li className={ruleChecks.length ? 'text-green-600' : 'text-gray-400'}>
                  {ruleChecks.length ? <Check className="w-3.5 h-3.5 inline mr-1.5" /> : <X className="w-3.5 h-3.5 inline mr-1.5" />}
                  At least {MIN_PASSWORD_LENGTH} characters
                </li>
                <li className={ruleChecks.uppercase ? 'text-green-600' : 'text-gray-400'}>
                  {ruleChecks.uppercase ? <Check className="w-3.5 h-3.5 inline mr-1.5" /> : <X className="w-3.5 h-3.5 inline mr-1.5" />}
                  One uppercase letter
                </li>
                <li className={ruleChecks.lowercase ? 'text-green-600' : 'text-gray-400'}>
                  {ruleChecks.lowercase ? <Check className="w-3.5 h-3.5 inline mr-1.5" /> : <X className="w-3.5 h-3.5 inline mr-1.5" />}
                  One lowercase letter
                </li>
                <li className={ruleChecks.number ? 'text-green-600' : 'text-gray-400'}>
                  {ruleChecks.number ? <Check className="w-3.5 h-3.5 inline mr-1.5" /> : <X className="w-3.5 h-3.5 inline mr-1.5" />}
                  One number
                </li>
                <li className={ruleChecks.special ? 'text-green-600' : 'text-gray-400'}>
                  {ruleChecks.special ? <Check className="w-3.5 h-3.5 inline mr-1.5" /> : <X className="w-3.5 h-3.5 inline mr-1.5" />}
                  One special character (!@#$%^&* etc.)
                </li>
              </ul>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900 mb-1.5">
                Confirm password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-11 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition"
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
              {confirmMismatch && (
                <p className="mt-1.5 text-sm text-amber-600">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </button>
          </form>

          <p className="text-center text-gray-500 text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-gray-900 font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
